import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  inferirDiasSemana, listarHojasCampania, parseHojaCampania, validarAlineacionCalendario,
  type CampaniaProgramada,
} from '../../domain/excel/importProgramacion';
import { emparejarTienda, type PdvBasico } from '../../domain/pdvMatch';
import { reconciliarBloque, type ReconciliacionBloque } from '../../domain/importBorrador';
import {
  createCampaign, findCampaignsByAnalyticAccount, listCampaignTypes, listCoordinators,
  upsertSchedule, type CoordinatorOption,
} from '../../api/campaigns';
import { createPdv, listPdv } from '../../api/pdv';
import { generatePositions } from '../../api/positions';
import { logAction } from '../../api/audit';
import { useAuth } from '../../components/AuthProvider';
import FilaTarjeta from '../../components/FilaTarjeta';
import type { CampaignRow, CampaignTypeRow } from '../../lib/db-types';

// Pantalla del "nivel 2" de docs/CARGA_PROGRAMACION.md: automatiza los pasos 2 a 6 del
// procedimiento manual y deja el 7 (dejar constancia) en el audit_log.
//
// Lo que manda aquí es la RECONCILIACIÓN: el recuadro compara lo que el sistema va a
// generar contra lo que declara el propio Excel. Si no cuadra no se importa —no se corrige
// el número, se busca por qué difiere—, porque un error de lectura del calendario no falla:
// genera los días equivocados sin decir nada.

const HORA_INICIO = '11:00';
const HORA_FIN = '19:00';

const NOMBRE_MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
  'agosto', 'setiembre', 'octubre', 'noviembre', 'diciembre'];

const NOMBRE_DIA = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

const FECHA = /^\d{4}-\d{2}-\d{2}$/;

/** El patrón en palabras ("todos menos martes"), que es como lo dice el comercial y como
 *  se revisa de un vistazo; la lista cruda de días no se puede verificar mirándola. */
function patronDias(weekdays: number[] | null): string {
  if (weekdays === null) return 'patrón no semanal';
  if (weekdays.length === 7) return 'todos los días';
  if (weekdays.length === 6) {
    const falta = [1, 2, 3, 4, 5, 6, 7].find((d) => !weekdays.includes(d)) as number;
    return `todos menos ${NOMBRE_DIA[falta - 1]}`;
  }
  const nombres = weekdays.map((d) => NOMBRE_DIA[d - 1]);
  return nombres.length === 1
    ? nombres[0]
    : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

const DIACRITICOS = /[\u0300-\u036f]/g;

function sinTildes(t: string): string {
  return t.normalize('NFD').replace(DIACRITICOS, '').trim().toUpperCase();
}

function ultimoDiaDelMes(anio: number, mes: number): number {
  return new Date(Date.UTC(anio, mes, 0, 12, 0, 0)).getUTCDate();
}

const fechaISO = (anio: number, mes: number, dia: number): string =>
  `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

const soles = (n: number): string => `S/ ${n.toLocaleString('es-PE')}`;

/** Código provisional para una tienda que no está en la maestra (docs §3.3). */
function codigoProvisional(nombre: string): string {
  const limpio = sinTildes(nombre).replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '');
  return limpio === '' ? 'PROV-TIENDA' : limpio.slice(0, 40);
}

// --- borrador editable ------------------------------------------------------

interface TiendaBorrador {
  id: string;
  nombre: string;
  cadena: string;
  region: string;
  dias: number[];
  /** Días de semana inferidos; null = patrón no semanal, no representable como horario. */
  weekdays: number[] | null;
  pdv: PdvBasico | null;
  codigoNuevo: string;
  incluida: boolean;
  crear: boolean;
}

interface BloqueBorrador {
  indice: number;
  tarifa: number;
  declaradas: number | null;
  horaInicio: string;
  horaFin: string;
  recursos: number;
  tiendas: TiendaBorrador[];
}

interface Borrador {
  cuenta: string;
  cliente: string;
  marca: string;
  tipo: string;
  descripcion: string;
  inicio: string;
  fin: string;
  coordinadorId: string;
  bloques: BloqueBorrador[];
}

function tipoPorDefecto(c: CampaniaProgramada, tipos: CampaignTypeRow[]): string {
  const buscado = sinTildes(c.dinamica);
  const hallado = tipos.find(
    (t) => sinTildes(t.code) === buscado || sinTildes(t.name) === buscado,
  );
  return hallado?.code ?? tipos[0]?.code ?? 'otro';
}

function coordinadorPorDefecto(c: CampaniaProgramada, opciones: CoordinatorOption[]): string {
  const buscado = sinTildes(c.coordinador);
  if (buscado === '') return '';
  const hallado = opciones.find(
    (o) => sinTildes(`${o.first_names} ${o.last_names}`) === buscado,
  );
  return hallado?.id ?? '';
}

/** Lo que el Excel trae y la campaña no tiene dónde guardar: categoría, dinámica,
 *  coordinador sin usuario y las jornadas del supervisor (que no se cargan como posiciones). */
function descripcionPorDefecto(c: CampaniaProgramada): string {
  const partes = [c.campania || c.cuentaAnalitica];
  if (c.categoria !== '') partes.push(`Categoría: ${c.categoria}`);
  if (c.dinamica !== '') partes.push(`Dinámica: ${c.dinamica}`);
  if (c.coordinador !== '') partes.push(`Coordinador del Excel: ${c.coordinador}`);
  const conSupervisor = c.bloques.filter((b) => b.totalDiasSupervisorDeclarado !== null);
  if (conSupervisor.length > 0) {
    const dias = conSupervisor.reduce((s, b) => s + (b.totalDiasSupervisorDeclarado ?? 0), 0);
    const tarifa = conSupervisor[0].rateAmountSupervisor;
    partes.push(
      `Supervisor${tarifa === null ? '' : ` ${soles(tarifa)} diario`}: ${dias} jornadas ` +
      'declaradas en el Excel (no se cargan como posiciones)',
    );
  }
  return partes.join(' · ');
}

function construirBorrador(
  c: CampaniaProgramada, pdvs: PdvBasico[], tipos: CampaignTypeRow[], coords: CoordinatorOption[],
): Borrador {
  return {
    cuenta: c.cuentaAnalitica,
    cliente: c.cliente,
    marca: c.marca,
    tipo: tipoPorDefecto(c, tipos),
    descripcion: descripcionPorDefecto(c),
    inicio: fechaISO(c.anio, c.mes, 1),
    fin: fechaISO(c.anio, c.mes, ultimoDiaDelMes(c.anio, c.mes)),
    coordinadorId: coordinadorPorDefecto(c, coords),
    bloques: c.bloques.map((b, i) => ({
      indice: i,
      tarifa: b.rateAmountCaptador,
      declaradas: b.totalDiasCaptadorDeclarado,
      horaInicio: HORA_INICIO,
      horaFin: HORA_FIN,
      recursos: 1,
      tiendas: b.tiendas.map((t, j) => {
        const pdv = emparejarTienda(t.nombre, pdvs);
        return {
          id: `${i}-${j}`,
          nombre: t.nombre,
          cadena: t.cadena,
          region: t.region,
          dias: t.dias,
          weekdays: inferirDiasSemana(t.dias, c.anio, c.mes),
          pdv,
          codigoNuevo: codigoProvisional(t.nombre),
          incluida: true,
          crear: false,
        };
      }),
    })),
  };
}

// --- textos de la reconciliación -------------------------------------------

function textoBloque(b: BloqueBorrador, r: ReconciliacionBloque): string {
  const cuenta = r.diasPorTienda === null
    ? `${r.tiendas} tiendas`
    : `${r.tiendas} tiendas × ${r.diasPorTienda} días${b.recursos > 1 ? ` × ${b.recursos} recursos` : ''}`;
  const declara = r.declaradas === null
    ? 'el Excel no declara su total'
    : `el Excel declara ${r.declaradas}`;
  const veredicto = r.cuadra
    ? ' ✓'
    : r.diferencia === null
      ? ''
      : ` · difiere en ${r.diferencia > 0 ? '+' : '−'}${Math.abs(r.diferencia)}`;
  return `Bloque ${b.indice + 1}: ${cuenta} = ${r.jornadas} jornadas · ${declara}${veredicto}`;
}

interface ResultadoImport {
  campaignId: string;
  horarios: number;
  tiendasCreadas: string[];
  insertadas: number;
  generadas: number;
  declaradas: number | null;
}

// --- tarjeta de una campaña -------------------------------------------------

function TarjetaCampania({
  hoja, campania, avisos, pdvs, tipos, coordinadores, puedeCrearPdv, maestraOk,
}: {
  hoja: string;
  campania: CampaniaProgramada;
  avisos: { rowNumber: number; message: string }[];
  pdvs: PdvBasico[];
  tipos: CampaignTypeRow[];
  coordinadores: CoordinatorOption[];
  puedeCrearPdv: boolean;
  maestraOk: boolean;
}) {
  const { user } = useAuth();
  const [borrador, setBorrador] = useState<Borrador>(
    () => construirBorrador(campania, pdvs, tipos, coordinadores),
  );
  const [existentes, setExistentes] = useState<CampaignRow[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [progreso, setProgreso] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoImport | null>(null);

  // Aviso de carga repetida: la cuenta analítica se repite todos los meses, así que el
  // duplicado solo se detecta cruzando cuenta + periodo.
  useEffect(() => {
    findCampaignsByAnalyticAccount(borrador.cuenta, borrador.inicio, borrador.fin)
      .then(setExistentes)
      .catch(() => setExistentes([]));
  }, [borrador.cuenta, borrador.inicio, borrador.fin]);

  function editar(patch: Partial<Borrador>) {
    setBorrador((b) => ({ ...b, ...patch }));
  }
  function editarBloque(i: number, patch: Partial<BloqueBorrador>) {
    setBorrador((b) => ({
      ...b,
      bloques: b.bloques.map((bl, j) => (j === i ? { ...bl, ...patch } : bl)),
    }));
  }
  function editarTienda(id: string, patch: Partial<TiendaBorrador>) {
    setBorrador((b) => ({
      ...b,
      bloques: b.bloques.map((bl) => ({
        ...bl,
        tiendas: bl.tiendas.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      })),
    }));
  }

  const reconciliaciones = borrador.bloques.map((b) =>
    reconciliarBloque(
      b.tiendas.filter((t) => t.incluida).map((t) => t.weekdays),
      b.recursos, borrador.inicio, borrador.fin, b.declaradas,
    ));
  const jornadas = reconciliaciones.reduce((s, r) => s + r.jornadas, 0);
  const declaradas = reconciliaciones.some((r) => r.declaradas === null)
    ? null
    : reconciliaciones.reduce((s, r) => s + (r.declaradas ?? 0), 0);
  const cuadraTodo = reconciliaciones.length > 0 && reconciliaciones.every((r) => r.cuadra);
  const costo = borrador.bloques.reduce(
    (s, b, i) => s + reconciliaciones[i].jornadas * b.tarifa, 0,
  );

  const todas = borrador.bloques.flatMap((b) => b.tiendas);
  const sinCruce = todas.filter((t) => t.pdv === null);
  const sinDecidir = sinCruce.filter((t) => t.incluida && !t.crear);
  const sinPatron = todas.filter((t) => t.incluida && t.weekdays === null);

  const impedimentos: string[] = [];
  if (!maestraOk) {
    impedimentos.push(
      'No se pudo leer la maestra de tiendas: sin ella el cruce no vale y la carga crearía '
      + 'tiendas duplicadas. Recarga la página antes de importar.',
    );
  }
  if (!cuadraTodo) {
    impedimentos.push(
      'La carga no reconcilia con el Excel. No se corrige el número: se busca por qué difiere ' +
      '(una tienda excluida, un día mal leído, un rango de fechas recortado).',
    );
  }
  if (sinDecidir.length > 0) {
    impedimentos.push(
      `${sinDecidir.length} tienda(s) no están en la maestra: créalas o exclúyelas de la carga.`,
    );
  }
  for (const t of sinPatron) {
    impedimentos.push(
      `«${t.nombre}» marca días que no forman un patrón semanal (${t.dias.join(', ')}): ` +
      'el sistema solo sabe programar por días de la semana. Exclúyela y cárgala a mano.',
    );
  }
  for (const b of borrador.bloques) {
    if (b.horaFin <= b.horaInicio) {
      impedimentos.push(`El horario del bloque ${b.indice + 1} termina antes de empezar.`);
    }
    if (b.recursos < 1) {
      impedimentos.push(`El bloque ${b.indice + 1} necesita al menos 1 recurso por día.`);
    }
  }
  if (todas.every((t) => !t.incluida)) impedimentos.push('No queda ninguna tienda incluida.');
  if (borrador.cliente.trim() === '' || borrador.marca.trim() === '') {
    impedimentos.push('El cliente y la marca son obligatorios.');
  }
  if (borrador.cuenta.trim() === '') {
    impedimentos.push('Falta la cuenta analítica: es el código con el que la operación nombra la campaña.');
  }
  if (!FECHA.test(borrador.inicio) || !FECHA.test(borrador.fin)) {
    impedimentos.push('Las fechas de la campaña están incompletas.');
  } else if (borrador.fin < borrador.inicio) {
    impedimentos.push('La fecha de fin es anterior a la de inicio.');
  }

  async function confirmar() {
    setGuardando(true);
    setError(null);
    let creada: CampaignRow | null = null;
    try {
      // 1. Tiendas que no están en la maestra: código provisional y SIN coordenadas
      //    (se capturan en terreno, ver docs/SETUP.md).
      const nuevas = new Map<string, string>();
      const aCrear = todas.filter((t) => t.incluida && t.pdv === null && t.crear);
      for (const [i, t] of aCrear.entries()) {
        setProgreso(`Creando tienda ${i + 1} de ${aCrear.length}…`);
        const fila = await createPdv({   // crear, nunca pisar: un código provisional repetido reescribía otra tienda
          code: t.codigoNuevo, name: t.nombre, chain: t.cadena || null, format: null,
          department: null, province: null, district: null, zone: t.region || null,
          address: null, lat: null, lng: null,
        });
        nuevas.set(t.id, fila.id);
      }

      // 2. La campaña, con las horas declaradas aquí: el Excel no las trae.
      setProgreso('Creando la campaña…');
      const horas = borrador.bloques
        .map((b) => `bloque ${b.indice + 1} ${b.horaInicio}-${b.horaFin}`).join(', ');
      creada = await createCampaign({
        client: borrador.cliente,
        brand: borrador.marca,
        type: borrador.tipo,
        description:
          `${borrador.descripcion} · Horario definido al importar (el Excel no trae horas): ${horas}`,
        quota: null,
        start_date: borrador.inicio,
        end_date: borrador.fin,
        comercial_id: user?.id ?? null,
        coordinator_id: borrador.coordinadorId === '' ? null : borrador.coordinadorId,
        status: 'borrador',
        analytic_account: borrador.cuenta,
      });

      // 3. Un horario por tienda × bloque (docs/CARGA_PROGRAMACION.md §4, paso 4).
      let horarios = 0;
      const total = borrador.bloques.reduce(
        (s, b) => s + b.tiendas.filter((t) => t.incluida).length, 0,
      );
      for (const b of borrador.bloques) {
        for (const t of b.tiendas) {
          if (!t.incluida || t.weekdays === null) continue;
          const pdvId = t.pdv?.id ?? nuevas.get(t.id);
          if (pdvId === undefined) continue;
          setProgreso(`Creando horario ${horarios + 1} de ${total}…`);
          await upsertSchedule({
            campaign_id: creada.id,
            pdv_id: pdvId,
            weekdays: t.weekdays,
            extra_dates: [],
            // Una campaña recién importada rige entera: la vigencia se recorta después, con «Dar de baja desde…».
            vigente_desde: null,
            vigente_hasta: null,
            start_time: b.horaInicio,
            end_time: b.horaFin,
            headcount: b.recursos,
            rate_amount: b.tarifa,
            rate_unit: 'jornada',
          });
          horarios += 1;
        }
      }

      // 4. Posiciones (idempotente) y 5. constancia en la auditoría.
      setProgreso('Generando las posiciones…');
      const { inserted, expanded } = await generatePositions(creada.id);
      const tiendasCreadas = aCrear.map((t) => t.codigoNuevo);
      await logAction('importar_programacion', 'campaign', creada.id, {
        hoja,
        cuenta_analitica: borrador.cuenta,
        horarios,
        tiendas_creadas: tiendasCreadas,
        tiendas_excluidas: todas.filter((t) => !t.incluida).map((t) => t.nombre),
        jornadas_generadas: expanded,
        jornadas_declaradas: declaradas,
        reconcilia: expanded === declaradas,
      });
      setResultado({
        campaignId: creada.id,
        horarios,
        tiendasCreadas,
        insertadas: inserted,
        generadas: expanded,
        declaradas,
      });
    } catch (e) {
      const detalle = e instanceof Error ? e.message : 'No se pudo importar la programación';
      setError(creada === null
        ? detalle
        : `${detalle}. La campaña ya se había creado: revísala y complétala o elimínala antes de reintentar.`);
    } finally {
      setGuardando(false);
      setProgreso('');
    }
  }

  if (resultado) {
    const reconcilia = resultado.declaradas !== null && resultado.generadas === resultado.declaradas;
    return (
      <section className="mb-4 rounded border border-green-300 bg-green-50 p-4">
        <h2 className="font-semibold">Campaña importada — {hoja}</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
          <li>
            <Link className="text-blue-600 underline" to={`/panel/campanias/${resultado.campaignId}`}>
              {borrador.cliente} — {borrador.marca} ({borrador.cuenta})
            </Link>{' '}
            creada en estado <strong>borrador</strong>: revísala y actívala desde Campañas.
          </li>
          <li>{resultado.horarios} horarios creados (uno por tienda × bloque).</li>
          {resultado.tiendasCreadas.length > 0 && (
            <li>
              {resultado.tiendasCreadas.length} tienda(s) creadas con código provisional y sin
              coordenadas: {resultado.tiendasCreadas.join(', ')}. Confirma su código real en la
              maestra y captura su ubicación en terreno.
            </li>
          )}
          <li>
            {resultado.generadas} posiciones (jornadas) generadas
            {resultado.insertadas !== resultado.generadas
              && ` · ${resultado.insertadas} nuevas, el resto ya existía`}.
          </li>
          <li className={reconcilia ? 'font-semibold text-green-800' : 'font-semibold text-red-700'}>
            {reconcilia
              ? `Reconciliación final: ${resultado.generadas} jornadas en la base de datos y el Excel declara ${resultado.declaradas} ✓`
              : `Reconciliación final: ${resultado.generadas} jornadas en la base de datos y el Excel declara ${resultado.declaradas ?? '—'}. REVISAR.`}
          </li>
          <li className="text-gray-600">Queda registrado en la auditoría.</li>
        </ul>
      </section>
    );
  }

  const campo = 'w-full rounded border px-2 py-1';

  return (
    <section className="mb-4 rounded border bg-white p-4">
      <h2 className="font-semibold">
        {hoja} — {campania.campania || 'Campaña sin nombre en el Excel'}
      </h2>
      <p className="mb-3 text-sm text-gray-600">
        {NOMBRE_MES[campania.mes - 1]} de {campania.anio} · {borrador.bloques.length} bloque(s) de
        tarifa · {todas.length} tiendas en la grilla
      </p>

      {existentes.length > 0 && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
          Ya existe una campaña con la cuenta <strong>{borrador.cuenta}</strong> en ese periodo:{' '}
          {existentes.map((c) => (
            <Link key={c.id} className="text-blue-600 underline" to={`/panel/campanias/${c.id}`}>
              {c.client} — {c.brand} ({c.start_date} a {c.end_date})
            </Link>
          ))}
          . Importar de nuevo la crearía por segunda vez.
        </div>
      )}

      {avisos.length > 0 && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="font-medium">El archivo tiene inconsistencias:</p>
          <ul className="mt-1 list-disc pl-5">
            {avisos.map((a) => <li key={`${a.rowNumber}-${a.message}`}>Fila {a.rowNumber}: {a.message}</li>)}
          </ul>
        </div>
      )}

      {/* Cabecera de la campaña */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">Cliente
          <input className={campo} value={borrador.cliente}
            onChange={(e) => editar({ cliente: e.target.value })} />
        </label>
        <label className="text-sm">Marca
          <input className={campo} value={borrador.marca}
            onChange={(e) => editar({ marca: e.target.value })} />
        </label>
        <label className="text-sm">Tipo de campaña
          <select className={campo} value={borrador.tipo}
            onChange={(e) => editar({ tipo: e.target.value })}>
            {tipos.length === 0 && <option value={borrador.tipo}>{borrador.tipo}</option>}
            {tipos.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
          </select>
        </label>
        <label className="text-sm">Cuenta analítica
          <input className={campo} value={borrador.cuenta}
            onChange={(e) => editar({ cuenta: e.target.value })} />
        </label>
        <label className="text-sm">Fecha inicio
          <input type="date" className={campo} value={borrador.inicio}
            onChange={(e) => editar({ inicio: e.target.value })} />
        </label>
        <label className="text-sm">Fecha fin
          <input type="date" className={campo} value={borrador.fin}
            onChange={(e) => editar({ fin: e.target.value })} />
        </label>
        <label className="text-sm sm:col-span-2">Coordinador
          <select className={campo} value={borrador.coordinadorId}
            onChange={(e) => editar({ coordinadorId: e.target.value })}>
            <option value="">— Sin coordinador con usuario —</option>
            {coordinadores.map((c) => (
              <option key={c.id} value={c.id}>{c.first_names} {c.last_names}</option>
            ))}
          </select>
        </label>
        <label className="text-sm sm:col-span-2">Descripción
          <textarea className={campo} rows={3} value={borrador.descripcion}
            onChange={(e) => editar({ descripcion: e.target.value })} />
        </label>
      </div>

      {/* Bloques de tarifa */}
      {borrador.bloques.map((b, i) => (
        <div key={b.indice} className="mb-4 rounded border p-3">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-medium">Bloque {b.indice + 1} · tarifa {soles(b.tarifa)} por jornada</h3>
            <span className="text-sm text-gray-600">
              {b.tiendas.filter((t) => t.incluida).length} de {b.tiendas.length} tiendas incluidas
            </span>
          </div>
          <div className="mb-3 grid gap-3 sm:grid-cols-3">
            <label className="text-sm">Hora de ingreso
              <input type="time" className={campo} value={b.horaInicio}
                onChange={(e) => editarBloque(i, { horaInicio: e.target.value })} />
            </label>
            <label className="text-sm">Hora de salida
              <input type="time" className={campo} value={b.horaFin}
                onChange={(e) => editarBloque(i, { horaFin: e.target.value })} />
            </label>
            <label className="text-sm">Recursos por día
              <input type="number" min={1} className={campo} value={b.recursos}
                onChange={(e) => editarBloque(i, { recursos: Number(e.target.value) })} />
            </label>
          </div>
          <p className="mb-2 text-xs text-gray-500">
            El Excel no trae horarios: 11:00-19:00 es el supuesto del piloto. El horario no
            afecta el pago (la tarifa es por jornada) pero sí decide qué cuenta como tardanza.
          </p>

          {/* Tiendas: tarjetas en celular, tabla en escritorio */}
          <div className="space-y-2 lg:hidden">
            {b.tiendas.map((t) => (
              <FilaTarjeta
                key={t.id}
                titulo={t.nombre}
                subtitulo={[t.cadena, t.region].filter((x) => x !== '').join(' · ')}
                atenuada={!t.incluida}
                seleccion={
                  <input type="checkbox" className="h-5 w-5" checked={t.incluida}
                    aria-label={`Incluir ${t.nombre}`}
                    onChange={(e) => editarTienda(t.id, { incluida: e.target.checked })} />
                }
                campos={[
                  { etiqueta: 'Días', valor: `${t.dias.length} · ${patronDias(t.weekdays)}` },
                  { etiqueta: 'Maestra', valor: textoCruce(t) },
                ]}
              />
            ))}
          </div>
          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full border text-sm">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="p-2">Incluir</th><th className="p-2">Tienda (Excel)</th>
                  <th className="p-2">Región</th><th className="p-2">Días</th>
                  <th className="p-2">Cruce con la maestra</th>
                </tr>
              </thead>
              <tbody>
                {b.tiendas.map((t) => (
                  <tr key={t.id} className={`border-t ${t.incluida ? '' : 'text-gray-400'}`}>
                    <td className="p-2">
                      <input type="checkbox" checked={t.incluida}
                        aria-label={`Incluir ${t.nombre}`}
                        onChange={(e) => editarTienda(t.id, { incluida: e.target.checked })} />
                    </td>
                    <td className="p-2 break-words">{t.nombre}</td>
                    <td className="p-2">{t.region}</td>
                    <td className="p-2">
                      {t.dias.length}
                      <span className={`block text-xs ${t.weekdays === null ? 'text-red-700' : 'text-gray-500'}`}>
                        {patronDias(t.weekdays)}
                      </span>
                    </td>
                    <td className={`p-2 ${t.pdv === null ? 'text-amber-700' : ''}`}>{textoCruce(t)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Tiendas que no están en la maestra */}
      {sinCruce.length > 0 && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3">
          <p className="font-medium">
            {sinCruce.length} tienda(s) de la programación no están en la maestra de PDV
          </p>
          <p className="mb-2 text-sm text-amber-900">
            No se inventan: o se crean con código provisional y sin coordenadas —para capturarlas
            en terreno— o se excluyen de esta carga (y entonces la reconciliación dejará de cuadrar).
          </p>
          {!puedeCrearPdv && (
            <p className="mb-2 text-sm text-amber-900">
              Solo el jefe de operaciones puede dar de alta tiendas: pídele que las cree, o
              exclúyelas de esta carga.
            </p>
          )}
          {sinCruce.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-200 py-2">
              <div className="min-w-0">
                <p className="break-words font-medium">{t.nombre}</p>
                <p className="text-xs text-amber-900">
                  {!t.incluida ? 'Excluida de esta carga'
                    : t.crear ? `Se creará con el código provisional ${t.codigoNuevo}, sin coordenadas`
                      : 'Sin decidir'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {puedeCrearPdv && (
                  <button type="button" className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
                    disabled={t.crear && t.incluida}
                    onClick={() => editarTienda(t.id, { crear: true, incluida: true })}>
                    Crear al vuelo
                  </button>
                )}
                <button type="button" className="rounded bg-gray-200 px-3 py-2 text-sm disabled:opacity-50"
                  disabled={!t.incluida}
                  onClick={() => editarTienda(t.id, { crear: false, incluida: false })}>
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* RECONCILIACIÓN: lo que decide si esto se puede importar o no */}
      <div className={`mb-4 rounded border-2 p-3 ${
        cuadraTodo ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
      }`}>
        <p className="font-semibold">
          {cuadraTodo ? 'La carga reconcilia con el Excel' : 'La carga NO reconcilia con el Excel'}
        </p>
        <ul className="mt-1 space-y-1 text-sm">
          {borrador.bloques.map((b, i) => (
            <li key={b.indice} className={reconciliaciones[i].cuadra ? '' : 'text-red-700'}>
              {textoBloque(b, reconciliaciones[i])}
            </li>
          ))}
          <li className={`font-semibold ${cuadraTodo ? 'text-green-800' : 'text-red-700'}`}>
            Total: {jornadas} jornadas · {declaradas === null
              ? 'el Excel no declara el total'
              : `el Excel declara ${declaradas}`}{cuadraTodo ? ' ✓' : ''}
          </li>
          <li className="text-gray-700">Costo de captadores: {soles(costo)}</li>
        </ul>
        {impedimentos.length > 0 && (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700">
            {impedimentos.map((m) => <li key={m}>{m}</li>)}
          </ul>
        )}
      </div>

      {error && <p className="mb-2 text-red-600">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
          disabled={guardando || impedimentos.length > 0}
          onClick={() => void confirmar()}
        >
          {guardando ? progreso || 'Importando…' : `Confirmar e importar ${hoja}`}
        </button>
      </div>
    </section>
  );
}

function textoCruce(t: TiendaBorrador): string {
  if (t.pdv !== null) return `${t.pdv.code} — ${t.pdv.name}`;
  if (!t.incluida) return 'Sin cruce · excluida';
  if (t.crear) return `Sin cruce · se creará como ${t.codigoNuevo}`;
  return 'Sin cruce con la maestra';
}

// --- pantalla ---------------------------------------------------------------

interface HojaDetectada {
  nombre: string;
  alineado: boolean;
  esperado: string;
  real: string;
}

interface Previsualizada {
  hoja: string;
  campania: CampaniaProgramada | null;
  avisos: { rowNumber: number; message: string }[];
  errores: { rowNumber: number; message: string }[];
}

export default function ImportarProgramacion() {
  const { roles } = useAuth();
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [hojas, setHojas] = useState<HojaDetectada[] | null>(null);
  const [elegidas, setElegidas] = useState<Record<string, boolean>>({});
  const [previas, setPrevias] = useState<Previsualizada[] | null>(null);
  const [pdvs, setPdvs] = useState<PdvBasico[]>([]);
  /** null = todavía cargando. Sin maestra el cruce marcaría TODAS las tiendas como nuevas
   *  y se crearían 19 duplicados: por eso su fallo bloquea la importación. */
  const [maestraOk, setMaestraOk] = useState<boolean | null>(null);
  const [tipos, setTipos] = useState<CampaignTypeRow[]>([]);
  const [coordinadores, setCoordinadores] = useState<CoordinatorOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const archivoRef = useRef<HTMLInputElement>(null);

  // Cada maestra se carga por su cuenta: que falte el catálogo de tipos no debe dejar sin
  // maestra de tiendas a la pantalla (ni al revés).
  useEffect(() => {
    const anotar = (m: string) => setAvisos((a) => (a.includes(m) ? a : [...a, m]));
    const detalle = (e: unknown): string => (e instanceof Error ? e.message : 'error desconocido');
    listPdv('')
      .then((p) => {
        setPdvs(p.map((x) => ({ id: x.id, code: x.code, name: x.name })));
        setMaestraOk(true);
        // Vacía no es un error, pero sí una trampa: sin tiendas ninguna cruza y la pantalla
        // propondría dar de alta la programación entera con códigos provisionales.
        if (p.length === 0) {
          anotar('La maestra de tiendas está vacía: ninguna tienda de la programación va a cruzar. Carga la maestra de PDV antes de importar.');
        }
      })
      .catch((e) => {
        setMaestraOk(false);
        anotar(`No se pudo cargar la maestra de tiendas: ${detalle(e)}. Sin ella el cruce marcaría todas las tiendas como nuevas, así que no se puede importar.`);
      });
    listCampaignTypes()
      .then((t) => {
        setTipos(t);
        if (t.length === 0) anotar('No hay tipos de campaña activos en la maestra: créalos antes de importar.');
      })
      .catch((e) => anotar(`No se pudo cargar la maestra de tipos de campaña: ${detalle(e)}`));
    listCoordinators().then(setCoordinadores)
      .catch((e) => anotar(`No se pudo cargar la lista de coordinadores: ${detalle(e)}`));
  }, []);

  async function onArchivo(file: File) {
    setError(null);
    setPrevias(null);
    setHojas(null);
    try {
      const data = await file.arrayBuffer();
      const nombres = listarHojasCampania(data);
      if (nombres.length === 0) {
        setError('El archivo no tiene ninguna pestaña de campaña (solo Plantilla y/o Resumen Programación).');
        return;
      }
      // La alineación del calendario se comprueba ANTES de nada: si la grilla está corrida,
      // la carga entera saldría mal sin dar ni un error.
      const detectadas = nombres.map((nombre) => ({ nombre, ...validarAlineacionCalendario(data, nombre) }));
      setBuffer(data);
      setHojas(detectadas);
      setElegidas(Object.fromEntries(detectadas.map((h) => [h.nombre, h.alineado])));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer el archivo');
    }
  }

  function previsualizar() {
    if (buffer === null || hojas === null) return;
    const seleccion = hojas.filter((h) => elegidas[h.nombre] && h.alineado);
    setPrevias(seleccion.map((h) => {
      const r = parseHojaCampania(buffer, h.nombre);
      return r.rows.length === 0
        ? { hoja: h.nombre, campania: null, avisos: [], errores: r.errors }
        : { hoja: h.nombre, campania: r.rows[0], avisos: r.errors, errores: [] };
    }));
  }

  const seleccionadas = (hojas ?? []).filter((h) => elegidas[h.nombre] && h.alineado).length;
  const puedeCrearPdv = roles.includes('admin');

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="mb-1 text-xl font-bold">Importar programación mensual</h1>
      <p className="mb-4 text-sm text-gray-600">
        Sube el Sheet del mes («Formato Programación RxH …», una pestaña por campaña) y el
        sistema arma el borrador de la campaña, sus horarios y sus jornadas. Solo se importa si
        los totales cuadran con los que declara el propio Excel.
      </p>

      {avisos.length > 0 && (
        <ul className="mb-3 space-y-1 rounded border border-amber-300 bg-amber-50 p-3 text-sm">
          {avisos.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}

      <section className="mb-4 rounded border bg-white p-4">
        <h2 className="mb-1 font-semibold">1. El archivo del mes</h2>
        <p className="mb-2 text-sm text-gray-600">
          Archivo .xlsx. Las pestañas «Plantilla» y «Resumen Programación» se ignoran solas.
        </p>
        <input
          ref={archivoRef} type="file" accept=".xlsx,.xls" className="max-w-full"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void onArchivo(f); }}
        />
        {error && <p className="mt-2 text-red-600">{error}</p>}
      </section>

      {hojas && (
        <section className="mb-4 rounded border bg-white p-4">
          <h2 className="mb-1 font-semibold">2. Qué campañas importar</h2>
          <p className="mb-2 text-sm text-gray-600">
            {hojas.length} pestaña(s) de campaña detectadas.
          </p>
          <ul className="mb-3 space-y-2">
            {hojas.map((h) => (
              <li key={h.nombre} className={`rounded border p-2 ${h.alineado ? '' : 'border-red-300 bg-red-50'}`}>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox" className="mt-1 h-4 w-4" checked={!!elegidas[h.nombre]}
                    disabled={!h.alineado}
                    onChange={(e) => setElegidas((s) => ({ ...s, [h.nombre]: e.target.checked }))}
                  />
                  <span className="min-w-0">
                    <span className="font-medium">{h.nombre}</span>
                    {!h.alineado && (
                      <span className="block text-red-700">
                        {h.esperado === '' || h.real === ''
                          ? 'No se pudo leer la grilla de esta pestaña (no se encontró la fila de días o el encabezado). No se puede importar.'
                          : `El encabezado dice que el día 1 es ${h.real}, pero en el calendario real es ${h.esperado}: el archivo está desalineado y la carga saldría mal, con todos los días corridos y sin dar ni un error. Corrige el archivo antes de importarlo.`}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
            disabled={seleccionadas === 0}
            onClick={previsualizar}
          >
            Previsualizar {seleccionadas} campaña(s)
          </button>
        </section>
      )}

      {previas && previas.length > 0 && (
        <>
          <h2 className="mb-2 font-semibold">3. Revisa, completa y confirma</h2>
          {previas.map((p) => (p.campania === null ? (
            <section key={p.hoja} className="mb-4 rounded border border-red-300 bg-red-50 p-4">
              <h3 className="font-semibold">{p.hoja} — no se pudo leer</h3>
              <ul className="mt-1 list-disc pl-5 text-sm text-red-700">
                {p.errores.map((e) => (
                  <li key={`${e.rowNumber}-${e.message}`}>
                    {e.rowNumber > 0 ? `Fila ${e.rowNumber}: ` : ''}{e.message}
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <TarjetaCampania
              key={p.hoja}
              hoja={p.hoja}
              campania={p.campania}
              avisos={p.avisos}
              pdvs={pdvs}
              tipos={tipos}
              coordinadores={coordinadores}
              puedeCrearPdv={puedeCrearPdv && maestraOk === true}
              maestraOk={maestraOk === true}
            />
          )))}
        </>
      )}
    </div>
  );
}
